import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Container, Form, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { useSelector } from "react-redux";
import Breadcrumb from "../common/breadcrumb";
import { C, tbl, reportingGlobalCSS } from "../reporting/reportingStyles";
import { Tile, RCard, Badge, Loader } from "../reporting/ReportingShared";
import { isMerchantScoped, isReadOnly } from "../../constants/roles";
import {
    syncCoupons,
    getCouponList,
    getCouponById,
    createCoupon,
    updateCoupon,
    deactivateCoupon,
    getCouponSyncConflicts,
    getCouponPendingRedemptions,
    getCouponRedemptions,
    resolveCouponConflict,
    getMerchantListing,
    getProductListing,
    getCategoryListing,
} from "../../utils/apiCalls";

const CHANNELS = ["pos", "pickup_app", "menu", "website"];
const SOURCES = ["admin", "odoo"];

const inputStyle = {
    padding: "10px 14px", borderRadius: 10, fontSize: 13,
    border: `1px solid ${C.border}`, background: C.white,
    color: C.text, outline: "none", width: "100%",
};
const btnPrimary = {
    padding: "10px 18px", borderRadius: 10, fontSize: 13, fontWeight: 700,
    border: "none", cursor: "pointer", color: C.white,
    background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`,
    boxShadow: "0 4px 12px rgba(0, 150, 137,0.25)",
};
const btnSecondary = {
    padding: "10px 18px", borderRadius: 10, fontSize: 13, fontWeight: 600,
    border: `1px solid ${C.border}`, cursor: "pointer", color: C.textMed, background: C.white,
};
const iconAction = {
    width: 30, height: 30, borderRadius: 8, display: "inline-flex",
    alignItems: "center", justifyContent: "center", cursor: "pointer",
    border: `1px solid ${C.border}`, background: C.white, marginRight: 6,
    fontSize: 13,
};

const formatDate = (d) => {
    if (!d) return "—";
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return "—";
    return dt.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

// "5 min ago", "2 hours ago", "Jun 5". Mirrors common at-a-glance freshness UX.
const formatRelative = (d) => {
    if (!d) return null;
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return null;
    const diffMs = Date.now() - dt.getTime();
    if (diffMs < 0) return "just now";
    const sec = Math.floor(diffMs / 1000);
    if (sec < 60) return "just now";
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min} min ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr} hour${hr === 1 ? "" : "s"} ago`;
    const day = Math.floor(hr / 24);
    if (day < 7) return `${day} day${day === 1 ? "" : "s"} ago`;
    return dt.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

const SourceBadge = ({ source }) =>
    source === "odoo"
        ? <Badge color={C.blue} bg={C.blueLight}>Merchant</Badge>
        : <Badge color={C.coral} bg={C.coralLight}>Admin</Badge>;

const ActiveBadge = ({ active }) =>
    active
        ? <Badge color={C.green} bg={C.greenLight}>● Active</Badge>
        : <Badge color={C.textLight} bg={C.borderLight}>● Inactive</Badge>;

// ───────────────────────────────────────────────────────────────────
const emptyForm = () => ({
    code: "",
    name: "",
    description: "",
    channels: ["pickup_app"],
    discount_type: "amount",
    amount: "",
    percentage: "",
    max_amount_per_order: "",
    min_order_amount: "",
    coupon_scope: "order",
    applicable_product_ids: [],
    applicable_category_ids: [],
    max_redemptions: "",
    starts_at: "",
    expires_at: "",
    active: true,
});

const initialFilters = {
    source: "",
    active: "true",
    channel: "",
    search: "",
};

const Coupons = () => {
    const userType = localStorage.getItem("userType");
    const merchantID = localStorage.getItem("MerchantId");
    const readOnly = isReadOnly(userType);
    const { selectedMerchant: reduxSelectedMerchant } = useSelector(s => s.merchant || {});
    const selectedMerchant = isMerchantScoped(userType) ? merchantID : reduxSelectedMerchant;

    const [merchantList, setMerchantList] = useState([]);
    const [coupons, setCoupons] = useState([]);
    // Server-side pagination for the coupons list. Page state is separate
    // from filter state so we can reset to 1 cleanly when filters change.
    const [page, setPage] = useState(1);
    const pageSize = 20;
    const [couponsTotal, setCouponsTotal] = useState(0);
    const [couponsTotalPages, setCouponsTotalPages] = useState(1);
    // Merchant-wide aggregate counts for the header tiles (ignore filters
    // so the tiles stay informative regardless of the current view).
    const [couponsStats, setCouponsStats] = useState({ total: 0, active: 0, odoo: 0 });
    const [conflicts, setConflicts] = useState([]);
    const [pendingPushes, setPendingPushes] = useState([]);
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [syncing, setSyncing] = useState(false);

    const [filters, setFilters] = useState(initialFilters);
    const [tempFilters, setTempFilters] = useState(initialFilters);
    const [filterModalOpen, setFilterModalOpen] = useState(false);

    const [editing, setEditing] = useState(null);    // null=closed, {}=create, object=edit
    const [form, setForm] = useState(emptyForm());
    const [saving, setSaving] = useState(false);
    const [conflictsOpen, setConflictsOpen] = useState(false);
    const [resolvingConflictId, setResolvingConflictId] = useState(null);
    const [pendingPushesOpen, setPendingPushesOpen] = useState(false);

    // Coupon view modal — read-only details + paginated redemption history.
    // Separate state from `editing` (the edit/create modal) so the two can't
    // collide if the user clicks both icons quickly.
    const [viewing, setViewing] = useState(null);
    const [redemptions, setRedemptions] = useState({
        rows: [],
        total: 0,
        page: 1,
        pageSize: 20,
        totalPages: 1,
    });
    const [redemptionsLoading, setRedemptionsLoading] = useState(false);

    // Holds the post-sync refresh timer so we can clear it on unmount.
    const syncRefreshTimerRef = useRef(null);
    useEffect(() => () => {
        if (syncRefreshTimerRef.current) clearTimeout(syncRefreshTimerRef.current);
    }, []);

    const merchantById = useMemo(() => {
        const m = new Map();
        merchantList.forEach(x => m.set(String(x.id), x));
        return m;
    }, [merchantList]);

    // Load merchant list for ALL authenticated users — the backend's
    // getMerchantList endpoint scopes to the caller's own merchant when
    // userType === 'merchant', so it returns just that one row safely.
    // We need it on both roles to look up the merchant's `reference`
    // for the Sync button (merchantReference isn't otherwise persisted).
    // Coerce to array — the wrapper returns undefined on error.
    // Skipped for read-only roles: the list only feeds the Sync button, which is
    // hidden for them, and the endpoint 403s for anything but admin/merchant.
    useEffect(() => {
        if (readOnly) return;
        (async () => setMerchantList((await getMerchantListing()) || []))();
    }, [readOnly]);

    const loadFormOptions = useCallback(async () => {
        if (!selectedMerchant || selectedMerchant === "all") return;
        const [p, c] = await Promise.all([
            getProductListing(selectedMerchant, {}, 1, 1000),
            getCategoryListing(selectedMerchant),
        ]);
        setProducts(Array.isArray(p?.products) ? p.products : Array.isArray(p) ? p : []);
        setCategories(Array.isArray(c) ? c : []);
    }, [selectedMerchant]);

    const fetchCoupons = useCallback(async () => {
        if (!selectedMerchant || selectedMerchant === "all") {
            setCoupons([]);
            setCouponsTotal(0);
            setCouponsTotalPages(1);
            return;
        }
        setIsLoading(true);
        const params = { merchantId: selectedMerchant, page, pageSize };
        if (filters.source) params.source = filters.source;
        if (filters.active !== "") params.active = filters.active;
        if (filters.channel) params.channel = filters.channel;
        if (filters.search) params.search = filters.search;
        const data = await getCouponList(params);
        setCoupons(data.rows || []);
        setCouponsTotal(data.total || 0);
        setCouponsTotalPages(data.totalPages || 1);
        setCouponsStats(data.stats || { total: 0, active: 0, odoo: 0 });
        setIsLoading(false);
    }, [selectedMerchant, filters, page]);

    const fetchConflicts = useCallback(async () => {
        if (!selectedMerchant || selectedMerchant === "all") {
            setConflicts([]);
            return;
        }
        const data = await getCouponSyncConflicts({ merchantId: selectedMerchant, resolved: false });
        setConflicts(data || []);
    }, [selectedMerchant]);

    const fetchPendingPushes = useCallback(async () => {
        if (!selectedMerchant || selectedMerchant === "all") {
            setPendingPushes([]);
            return;
        }
        const data = await getCouponPendingRedemptions({ merchantId: selectedMerchant });
        setPendingPushes(data || []);
    }, [selectedMerchant]);

    useEffect(() => { fetchCoupons(); fetchConflicts(); fetchPendingPushes(); loadFormOptions(); }, [fetchCoupons, fetchConflicts, fetchPendingPushes, loadFormOptions]);

    useEffect(() => {
        if (filterModalOpen) setTempFilters(filters);
    }, [filterModalOpen, filters]);

    const applyFilters = () => {
        setFilters(tempFilters);
        setFilterModalOpen(false);
    };
    const clearFilters = () => {
        setTempFilters(initialFilters);
        setFilters(initialFilters);
        setFilterModalOpen(false);
    };

    // Reset to page 1 whenever filters or the selected merchant change.
    // Without this, a user on page 5 who filters to "active only" with 0
    // matches would see an empty page and no way back to a valid page.
    useEffect(() => {
        setPage(1);
    }, [filters, selectedMerchant]);

    const tiles = useMemo(() => {
        // Tile totals come from server-side aggregates (merchant-wide,
        // unfiltered). The list table below shows only what matches filters.
        return {
            total: couponsStats.total,
            active: couponsStats.active,
            odoo: couponsStats.odoo,
            conflicts: conflicts.length,
            pendingPushes: pendingPushes.length,
        };
    }, [couponsStats, conflicts, pendingPushes]);

    const activeFilterPills = useMemo(() => {
        const pills = [];
        if (filters.source) pills.push({ key: "source", label: "Source", value: filters.source });
        if (filters.active !== "true" && filters.active !== "") {
            pills.push({ key: "active", label: "Status", value: filters.active === "false" ? "Inactive" : filters.active });
        }
        if (filters.channel) pills.push({ key: "channel", label: "Channel", value: filters.channel });
        if (filters.search) pills.push({ key: "search", label: "Code", value: filters.search });
        return pills;
    }, [filters]);

    const removeFilter = (key) => {
        const next = { ...filters };
        if (key === "active") next.active = "true";
        else next[key] = "";
        setFilters(next);
    };

    const onSync = async () => {
        const merchant = merchantById.get(String(selectedMerchant));
        const reference = merchant?.reference;
        if (!reference) return;
        setSyncing(true);
        try {
            await syncCoupons(reference);
        } finally {
            setSyncing(false);
            // Backend debounces 40s before the sync actually runs. Refresh
            // after 45s. Ref-tracked so unmount cleanup can cancel it.
            if (syncRefreshTimerRef.current) clearTimeout(syncRefreshTimerRef.current);
            syncRefreshTimerRef.current = setTimeout(async () => {
                fetchCoupons();
                fetchConflicts();
                fetchPendingPushes();
                // Refresh the merchant list to pick up the new last_coupon_sync_at.
                setMerchantList((await getMerchantListing()) || []);
                syncRefreshTimerRef.current = null;
            }, 45 * 1000);
        }
    };

    const openCreate = () => {
        setForm(emptyForm());
        setEditing({});
    };

    // Open the read-only view modal for a coupon. Resets redemption pagination
    // to page 1 — the useEffect below picks up `viewing.id` and `redemptions.page`
    // and fetches the right slice.
    const openView = (couponRow) => {
        setRedemptions({ rows: [], total: 0, page: 1, pageSize: 20, totalPages: 1 });
        setViewing(couponRow);
    };

    // Re-fetch redemption history when the viewed coupon changes OR when the
    // user clicks a different page. Keep page state in `redemptions` so we
    // don't need a separate piece of state for it.
    useEffect(() => {
        if (!viewing?.id) return;
        let cancelled = false;
        setRedemptionsLoading(true);
        (async () => {
            const data = await getCouponRedemptions(viewing.id, {
                page: redemptions.page,
                pageSize: redemptions.pageSize,
            });
            if (cancelled) return;
            setRedemptions(data);
            setRedemptionsLoading(false);
        })();
        return () => { cancelled = true; };
    }, [viewing?.id, redemptions.page, redemptions.pageSize]);

    const openEdit = async (couponRow) => {
        // Always fetch the full coupon by id — the list endpoint doesn't
        // include applicableProducts / applicableCategories, so editing
        // from list data would wipe the junctions on save.
        const coupon = await getCouponById(couponRow.id);
        if (!coupon) return;  // error toast already shown by API wrapper
        setForm({
            code: coupon.code || "",
            name: coupon.name || "",
            description: coupon.description || "",
            channels: Array.isArray(coupon.channels) ? coupon.channels : ["pickup_app"],
            discount_type: coupon.discount_type || "amount",
            amount: coupon.amount ?? "",
            percentage: coupon.percentage ?? "",
            max_amount_per_order: coupon.max_amount_per_order ?? "",
            min_order_amount: coupon.min_order_amount ?? "",
            coupon_scope: coupon.coupon_scope || "order",
            applicable_product_ids: Array.isArray(coupon.applicableProducts) ? coupon.applicableProducts.map(p => p.id) : [],
            applicable_category_ids: Array.isArray(coupon.applicableCategories) ? coupon.applicableCategories.map(c => c.id) : [],
            max_redemptions: coupon.max_redemptions ?? "",
            starts_at: coupon.starts_at ? new Date(coupon.starts_at).toISOString().slice(0, 10) : "",
            expires_at: coupon.expires_at ? new Date(coupon.expires_at).toISOString().slice(0, 10) : "",
            active: !!coupon.active,
            _id: coupon.id,
            _source: coupon.source,
        });
        setEditing(coupon);
    };

    const buildPayload = () => {
        const p = {
            merchantId: selectedMerchant,
            code: form.code?.trim(),
            name: form.name || null,
            description: form.description || null,
            channels: form.channels,
            discount_type: form.discount_type,
            amount: Number(form.amount) || 0,
            percentage: form.discount_type === "percentage"
                ? (form.percentage === "" ? null : Number(form.percentage))
                : null,
            max_amount_per_order: form.max_amount_per_order === "" ? null : Number(form.max_amount_per_order),
            min_order_amount: form.min_order_amount === "" ? null : Number(form.min_order_amount),
            coupon_scope: form.coupon_scope,
            max_redemptions: form.max_redemptions === "" ? null : Number(form.max_redemptions),
            starts_at: form.starts_at || null,
            expires_at: form.expires_at || null,
            active: !!form.active,
        };
        if (form.coupon_scope === "product") {
            p.applicable_product_ids = form.applicable_product_ids.map(Number);
            p.applicable_category_ids = form.applicable_category_ids.map(Number);
        }
        return p;
    };

    const onSave = async () => {
        setSaving(true);
        try {
            const payload = buildPayload();
            const result = form._id
                ? await updateCoupon(form._id, payload)
                : await createCoupon(payload);
            if (result) {
                setEditing(null);
                fetchCoupons();
            }
        } finally {
            setSaving(false);
        }
    };

    const onDeactivate = async (coupon) => {
        if (!window.confirm(`Deactivate coupon "${coupon.code}"?`)) return;
        const ok = await deactivateCoupon(coupon.id);
        if (ok) fetchCoupons();
    };

    const onResolveConflict = async (conflict) => {
        // Guard against double-click — disable the button per-row while in-flight.
        if (resolvingConflictId === conflict.id) return;
        setResolvingConflictId(conflict.id);
        try {
            const ok = await resolveCouponConflict(conflict.id);
            if (ok) fetchConflicts();
        } finally {
            setResolvingConflictId(null);
        }
    };

    const isOdooEdit = form._source === "odoo";
    const activeFilterCount = activeFilterPills.length;
    const merchantSelected = selectedMerchant && selectedMerchant !== "all";

    // At-a-glance freshness indicator. Reads Merchants.last_coupon_sync_at,
    // refreshed after each manual sync via refreshMerchantList().
    const currentMerchant = merchantById.get(String(selectedMerchant));
    const lastSyncedLabel = currentMerchant?.last_coupon_sync_at
        ? formatRelative(currentMerchant.last_coupon_sync_at)
        : null;
    // Sync (both manual and cron) is gated on Merchants.coupons_enabled.
    // We disable the Sync button up-front so admins don't get a 422 surprise.
    const couponsSyncEnabled = !!currentMerchant?.coupons_enabled;

    // Save-button validation. For Merchant-synced coupons only channels matter
    // (everything else is locked); for admin coupons we enforce the same
    // rules the backend enforces, so the user gets feedback before submit.
    // `amount` is the SAR ceiling for both types; `percentage` is required
    // only when discount_type='percentage'.
    const amountNum = Number(form.amount);
    const percentageNum = Number(form.percentage);
    const channelsOk = Array.isArray(form.channels) && form.channels.length > 0;
    const codeOk = !!form.code?.trim();
    const amountOk = Number.isFinite(amountNum) && amountNum > 0;
    const percentageOk = form.discount_type !== "percentage"
        || (Number.isFinite(percentageNum) && percentageNum > 0 && percentageNum <= 100);
    const canSave = isOdooEdit
        ? channelsOk
        : (codeOk && amountOk && percentageOk && channelsOk);
    const saveDisabledReason = !codeOk ? "Code is required"
        : !amountOk ? "Amount (SAR ceiling) must be greater than 0"
            : !percentageOk ? "Percentage must be between 0 and 100"
                : !channelsOk ? "Pick at least one channel"
                    : "";

    return (
        <Fragment>
            <Breadcrumb title="Coupons" parent="Loyalty" />
            <Container fluid>
                <style>{reportingGlobalCSS}</style>
                <div className="rpt-wrap">

                    {/* Tiles */}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginBottom: 20 }}>
                        <Tile label="Coupons" value={tiles.total} sub="All sources" icon="🎟️" gradient={`linear-gradient(135deg, ${C.coral}, #00bfa5)`} delay={0} />
                        <Tile label="Active" value={tiles.active} sub="Currently redeemable" icon="●" gradient={`linear-gradient(135deg, ${C.green}, #38F9D7)`} delay={80} />
                        <Tile label="Synced from Merchant" value={tiles.odoo} sub="Owned by merchant POS" icon="⟳" gradient={`linear-gradient(135deg, ${C.blue}, #A8B8FF)`} delay={160} />
                        <Tile label="Pending pushes" value={tiles.pendingPushes} sub={tiles.pendingPushes ? "Waiting for Odoo" : "All synced"} icon="↑" gradient={`linear-gradient(135deg, ${C.purple || "#8E6DDC"}, #B89BFF)`} delay={240} />
                        <Tile label="Conflicts" value={tiles.conflicts} sub={tiles.conflicts ? "Need review" : "All clear"} icon="!" gradient={`linear-gradient(135deg, ${C.orange}, #FFB347)`} delay={320} />
                    </div>

                    {/* Card */}
                    <RCard
                        title={
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", gap: 10, flexWrap: "wrap" }}>
                                <span>
                                    🎟️ Coupons
                                    <span style={{ color: C.textLight, fontWeight: 500, marginLeft: 8 }}>
                                        · {couponsTotal} {couponsTotal === 1 ? "match" : "matches"}
                                    </span>
                                    {merchantSelected && !couponsSyncEnabled && !readOnly && (
                                        <span style={{ color: C.coral, fontWeight: 600, marginLeft: 8, fontSize: 12 }}>
                                            · Sync disabled (enable from merchant settings)
                                        </span>
                                    )}
                                    {lastSyncedLabel && couponsSyncEnabled && (
                                        <span style={{ color: C.textLight, fontWeight: 500, marginLeft: 8, fontSize: 12 }}>
                                            · Last synced {lastSyncedLabel}
                                        </span>
                                    )}
                                </span>
                                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                    {/* Sync, conflict resolution and coupon CRUD all mutate — read-only
                                        roles keep only the filters and the per-row View action. */}
                                    {!readOnly && pendingPushes.length > 0 && (
                                        <button onClick={() => setPendingPushesOpen(true)} style={{ ...btnSecondary, color: C.purple || "#8E6DDC", borderColor: C.purple || "#8E6DDC" }}>
                                            ↑ {pendingPushes.length} pending push{pendingPushes.length === 1 ? "" : "es"}
                                        </button>
                                    )}
                                    {!readOnly && conflicts.length > 0 && (
                                        <button onClick={() => setConflictsOpen(true)} style={{ ...btnSecondary, color: C.coral, borderColor: C.coral }}>
                                            ⚠ {conflicts.length} conflict{conflicts.length === 1 ? "" : "s"}
                                        </button>
                                    )}
                                    {!readOnly && (
                                        <button
                                            onClick={onSync}
                                            style={btnSecondary}
                                            disabled={syncing || !merchantSelected || !couponsSyncEnabled}
                                            title={!couponsSyncEnabled && merchantSelected ? "Enable 'Coupons Sync' for this merchant from the merchant settings first." : ""}
                                        >
                                            <span className={syncing ? "rpt-spin" : ""} style={{ display: "inline-block", marginRight: 6 }}>⟳</span>
                                            {syncing ? "Syncing…" : "Sync from Merchant"}
                                        </button>
                                    )}
                                    <button onClick={() => setFilterModalOpen(true)} style={btnSecondary}>
                                        Filters {activeFilterCount > 0 && (
                                            <span style={{ background: C.coralLight, color: C.coralDark, padding: "2px 8px", borderRadius: 10, marginLeft: 6, fontSize: 11 }}>{activeFilterCount}</span>
                                        )}
                                    </button>
                                    {!readOnly && (
                                        <button onClick={openCreate} style={btnPrimary} disabled={!merchantSelected}>+ New Coupon</button>
                                    )}
                                </div>
                            </div>
                        }
                    >
                        {/* Active filter pills */}
                        {activeFilterPills.length > 0 && (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }}>
                                {activeFilterPills.map((p) => (
                                    <span
                                        key={p.key}
                                        onClick={() => removeFilter(p.key)}
                                        style={{
                                            display: "inline-flex", alignItems: "center", gap: 6,
                                            padding: "5px 10px", borderRadius: 8, fontSize: 12,
                                            background: C.coralLight, color: C.coralDark, fontWeight: 600,
                                            cursor: "pointer", border: `1px solid #e0f2f1`,
                                        }}
                                        title="Click to remove"
                                    >
                                        <strong>{p.label}:</strong> {p.value} <span style={{ opacity: 0.6 }}>×</span>
                                    </span>
                                ))}
                            </div>
                        )}

                        {!merchantSelected ? (
                            <div style={{ textAlign: "center", padding: 40, color: C.textMed }}>
                                Select a merchant from the header to view their coupons.
                            </div>
                        ) : isLoading ? (
                            <Loader />
                        ) : coupons.length === 0 ? (
                            <div style={{ textAlign: "center", padding: 40, color: C.textMed }}>No coupons found.</div>
                        ) : (
                            <div style={{ overflowX: "auto" }}>
                                <table style={tbl.table}>
                                    <thead>
                                        <tr>
                                            <th style={tbl.th}>Code</th>
                                            <th style={tbl.th}>Name</th>
                                            <th style={tbl.th}>Source</th>
                                            <th style={tbl.th}>Type</th>
                                            <th style={tbl.th}>Amount</th>
                                            <th style={tbl.th}>Scope</th>
                                            <th style={tbl.th}>Balance</th>
                                            <th style={tbl.th}>Uses</th>
                                            <th style={tbl.th}>Expires</th>
                                            <th style={tbl.th}>Status</th>
                                            <th style={tbl.th}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {coupons.map(c => (
                                            <tr key={c.id} className="rpt-row">
                                                <td style={{ ...tbl.td, fontFamily: "ui-monospace, SFMono-Regular, monospace", fontSize: 12, fontWeight: 700, color: C.text }}>
                                                    {c.code}
                                                </td>
                                                <td style={tbl.td}>{c.name || <span style={{ color: C.textLight }}>—</span>}</td>
                                                <td style={tbl.td}><SourceBadge source={c.source} /></td>
                                                <td style={{ ...tbl.td, color: C.textMed, textTransform: "capitalize" }}>{c.discount_type}</td>
                                                <td style={tbl.td}>
                                                    <strong>{c.discount_type === "percentage" ? `${c.percentage ?? 0}%` : c.amount}</strong>
                                                    {c.discount_type === "percentage" && c.amount != null && (
                                                        <span style={{ color: C.textLight, fontSize: 11 }}> · up to {c.amount}</span>
                                                    )}
                                                    {c.max_amount_per_order ? <span style={{ color: C.textLight, fontSize: 11 }}> · max {c.max_amount_per_order}/order</span> : ""}
                                                </td>
                                                <td style={{ ...tbl.td, color: C.textMed, textTransform: "capitalize" }}>{c.coupon_scope}</td>
                                                <td style={tbl.td}>
                                                    {c.remaining_balance != null
                                                        ? <span><strong>{c.remaining_balance}</strong> <span style={{ color: C.textLight }}>/ {c.amount}</span></span>
                                                        : <span style={{ color: C.textLight }}>—</span>}
                                                </td>
                                                <td style={tbl.td}>
                                                    {c.current_redemptions}
                                                    {c.max_redemptions ? <span style={{ color: C.textLight }}> / {c.max_redemptions}</span> : ""}
                                                </td>
                                                <td style={{ ...tbl.td, color: C.textMed, fontSize: 12, whiteSpace: "nowrap" }}>{formatDate(c.expires_at)}</td>
                                                <td style={tbl.td}><ActiveBadge active={c.active} /></td>
                                                <td style={tbl.td}>
                                                    <span style={iconAction} title="View" onClick={() => openView(c)}>👁</span>
                                                    {!readOnly && (
                                                        <span style={iconAction} title="Edit" onClick={() => openEdit(c)}>✎</span>
                                                    )}
                                                    {!readOnly && c.active && (
                                                        <span style={{ ...iconAction, color: C.coral }} title="Deactivate" onClick={() => onDeactivate(c)}>⊘</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Pagination — Prev/Next + indicator. Hidden when
                            there's only one page so it doesn't add noise. */}
                        {coupons.length > 0 && couponsTotalPages > 1 && (
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 14, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
                                <button
                                    style={{ ...btnSecondary, opacity: page <= 1 ? 0.5 : 1 }}
                                    disabled={page <= 1 || isLoading}
                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                >
                                    ← Prev
                                </button>
                                <span style={{ color: C.textMed, fontSize: 12 }}>
                                    Page {page} of {couponsTotalPages}
                                    <span style={{ color: C.textLight, marginLeft: 6 }}>
                                        ({couponsTotal} total)
                                    </span>
                                </span>
                                <button
                                    style={{ ...btnSecondary, opacity: page >= couponsTotalPages ? 0.5 : 1 }}
                                    disabled={page >= couponsTotalPages || isLoading}
                                    onClick={() => setPage(p => Math.min(couponsTotalPages, p + 1))}
                                >
                                    Next →
                                </button>
                            </div>
                        )}
                    </RCard>
                </div>
            </Container>

            {/* ─── Filter Modal ─── */}
            <Modal isOpen={filterModalOpen} toggle={() => setFilterModalOpen(false)}>
                <ModalHeader toggle={() => setFilterModalOpen(false)}>Filter Coupons</ModalHeader>
                <ModalBody>
                    <Form>
                        <FormGroup>
                            <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>SOURCE</Label>
                            <Input type="select" style={inputStyle} value={tempFilters.source}
                                onChange={(e) => setTempFilters({ ...tempFilters, source: e.target.value })}>
                                <option value="">All Sources</option>
                                {SOURCES.map(s => <option key={s} value={s}>{s === "odoo" ? "Merchant" : "Admin"}</option>)}
                            </Input>
                        </FormGroup>
                        <FormGroup>
                            <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>STATUS</Label>
                            <Input type="select" style={inputStyle} value={tempFilters.active}
                                onChange={(e) => setTempFilters({ ...tempFilters, active: e.target.value })}>
                                <option value="">All Statuses</option>
                                <option value="true">Active</option>
                                <option value="false">Inactive</option>
                            </Input>
                        </FormGroup>
                        <FormGroup>
                            <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>CHANNEL</Label>
                            <Input type="select" style={inputStyle} value={tempFilters.channel}
                                onChange={(e) => setTempFilters({ ...tempFilters, channel: e.target.value })}>
                                <option value="">All Channels</option>
                                {CHANNELS.map(ch => <option key={ch} value={ch}>{ch}</option>)}
                            </Input>
                        </FormGroup>
                        <FormGroup>
                            <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>CODE</Label>
                            <Input type="text" style={inputStyle} value={tempFilters.search}
                                onChange={(e) => setTempFilters({ ...tempFilters, search: e.target.value })}
                                placeholder="SAVE10..." />
                        </FormGroup>
                    </Form>
                </ModalBody>
                <ModalFooter style={{ borderTop: `1px solid ${C.border}` }}>
                    <button onClick={clearFilters} style={btnSecondary}>Clear</button>
                    <button onClick={applyFilters} style={btnPrimary}>Apply</button>
                </ModalFooter>
            </Modal>

            {/* ─── Create / Edit Modal ─── */}
            <Modal isOpen={!!editing} toggle={() => setEditing(null)} size="lg">
                <ModalHeader toggle={() => setEditing(null)}>
                    {form._id ? `Edit Coupon — ${form.code}` : "Create Coupon"}
                    {isOdooEdit && (
                        <span style={{ marginLeft: 10, fontSize: 12, color: C.coral, fontWeight: 600 }}>
                            (Merchant-synced — only channels &amp; status are editable)
                        </span>
                    )}
                </ModalHeader>
                <ModalBody>
                    <Form>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                            <FormGroup>
                                <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>CODE *</Label>
                                <Input style={inputStyle} value={form.code} disabled={isOdooEdit}
                                    onChange={(e) => setForm({ ...form, code: e.target.value })} />
                            </FormGroup>
                            <FormGroup>
                                <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>NAME</Label>
                                <Input style={inputStyle} value={form.name} disabled={isOdooEdit}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })} />
                            </FormGroup>
                        </div>

                        <FormGroup>
                            <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>DESCRIPTION</Label>
                            <Input type="textarea" style={{ ...inputStyle, minHeight: 60 }} value={form.description} disabled={isOdooEdit}
                                onChange={(e) => setForm({ ...form, description: e.target.value })} />
                        </FormGroup>

                        <FormGroup>
                            <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>CHANNELS</Label>
                            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", paddingTop: 4 }}>
                                {CHANNELS.map(ch => (
                                    <label key={ch} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 13, color: C.text }}>
                                        <input type="checkbox" checked={form.channels.includes(ch)}
                                            onChange={(e) => {
                                                const next = e.target.checked
                                                    ? [...form.channels, ch]
                                                    : form.channels.filter(x => x !== ch);
                                                setForm({ ...form, channels: next });
                                            }} />
                                        {ch}
                                    </label>
                                ))}
                            </div>
                        </FormGroup>

                        <div style={{ display: "grid", gridTemplateColumns: form.discount_type === "percentage" ? "1fr 1fr 1fr 1fr" : "1fr 1fr 1fr", gap: 12 }}>
                            <FormGroup>
                                <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>DISCOUNT TYPE *</Label>
                                <Input type="select" style={inputStyle} value={form.discount_type} disabled={isOdooEdit}
                                    onChange={(e) => setForm({ ...form, discount_type: e.target.value })}>
                                    <option value="amount">Amount (SAR)</option>
                                    <option value="percentage">Percentage</option>
                                </Input>
                            </FormGroup>
                            {form.discount_type === "percentage" && (
                                <FormGroup>
                                    <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>PERCENTAGE * (%)</Label>
                                    <Input type="number" step="0.01" min="0" max="100" style={inputStyle} value={form.percentage} disabled={isOdooEdit}
                                        onChange={(e) => setForm({ ...form, percentage: e.target.value })} />
                                </FormGroup>
                            )}
                            <FormGroup>
                                <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>
                                    AMOUNT * (SAR)
                                </Label>
                                <Input type="number" step="0.01" style={inputStyle} value={form.amount} disabled={isOdooEdit}
                                    onChange={(e) => setForm({ ...form, amount: e.target.value })} />
                            </FormGroup>
                            <FormGroup>
                                <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>MAX PER ORDER (SAR)</Label>
                                <Input type="number" step="0.01" style={inputStyle} value={form.max_amount_per_order} disabled={isOdooEdit}
                                    onChange={(e) => setForm({ ...form, max_amount_per_order: e.target.value })} />
                            </FormGroup>
                        </div>

                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                            <FormGroup>
                                <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>MIN ORDER (SAR)</Label>
                                <Input type="number" step="0.01" style={inputStyle} value={form.min_order_amount} disabled={isOdooEdit}
                                    onChange={(e) => setForm({ ...form, min_order_amount: e.target.value })} />
                            </FormGroup>
                            <FormGroup>
                                <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>MAX REDEMPTIONS</Label>
                                <Input type="number" style={inputStyle} value={form.max_redemptions} disabled={isOdooEdit}
                                    onChange={(e) => setForm({ ...form, max_redemptions: e.target.value })} />
                            </FormGroup>
                        </div>

                        <FormGroup>
                            <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>SCOPE</Label>
                            <Input type="select" style={inputStyle} value={form.coupon_scope} disabled={isOdooEdit}
                                onChange={(e) => setForm({ ...form, coupon_scope: e.target.value })}>
                                <option value="order">Whole order</option>
                                <option value="product">Specific products / categories</option>
                            </Input>
                        </FormGroup>

                        {form.coupon_scope === "product" && !isOdooEdit && (
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                                <FormGroup>
                                    <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>APPLICABLE PRODUCTS</Label>
                                    <Input type="select" multiple style={{ ...inputStyle, minHeight: 140 }}
                                        value={form.applicable_product_ids.map(String)}
                                        onChange={(e) => {
                                            const vals = Array.from(e.target.selectedOptions, o => Number(o.value));
                                            setForm({ ...form, applicable_product_ids: vals });
                                        }}>
                                        {products.map(p => (
                                            <option key={p.id} value={p.id}>{p.title || p.name || `#${p.id}`}</option>
                                        ))}
                                    </Input>
                                </FormGroup>
                                <FormGroup>
                                    <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>APPLICABLE CATEGORIES</Label>
                                    <Input type="select" multiple style={{ ...inputStyle, minHeight: 140 }}
                                        value={form.applicable_category_ids.map(String)}
                                        onChange={(e) => {
                                            const vals = Array.from(e.target.selectedOptions, o => Number(o.value));
                                            setForm({ ...form, applicable_category_ids: vals });
                                        }}>
                                        {categories.map(c => (
                                            <option key={c.id} value={c.id}>{c.name || `#${c.id}`}</option>
                                        ))}
                                    </Input>
                                </FormGroup>
                            </div>
                        )}

                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                            <FormGroup>
                                <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>STARTS AT</Label>
                                <Input type="date" style={inputStyle} value={form.starts_at} disabled={isOdooEdit}
                                    onChange={(e) => setForm({ ...form, starts_at: e.target.value })} />
                            </FormGroup>
                            <FormGroup>
                                <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>EXPIRES AT</Label>
                                <Input type="date" style={inputStyle} value={form.expires_at} disabled={isOdooEdit}
                                    onChange={(e) => setForm({ ...form, expires_at: e.target.value })} />
                            </FormGroup>
                            <FormGroup>
                                <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>STATUS</Label>
                                <label style={{ display: "flex", alignItems: "center", gap: 8, paddingTop: 10, cursor: "pointer", fontSize: 13, color: C.text }}>
                                    <input type="checkbox" checked={form.active}
                                        onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                                    Active
                                </label>
                            </FormGroup>
                        </div>
                    </Form>
                </ModalBody>
                <ModalFooter style={{ borderTop: `1px solid ${C.border}` }}>
                    <button onClick={() => setEditing(null)} style={btnSecondary} disabled={saving}>Cancel</button>
                    <button
                        onClick={onSave}
                        style={btnPrimary}
                        disabled={saving || !canSave}
                        title={!canSave ? saveDisabledReason : undefined}
                    >
                        {saving ? "Saving…" : (form._id ? "Save changes" : "Create coupon")}
                    </button>
                </ModalFooter>
            </Modal>

            {/* ─── Conflicts Modal ─── */}
            <Modal isOpen={conflictsOpen} toggle={() => setConflictsOpen(false)} size="lg">
                <ModalHeader toggle={() => setConflictsOpen(false)}>Unresolved sync conflicts</ModalHeader>
                <ModalBody>
                    {conflicts.length === 0 ? (
                        <div style={{ textAlign: "center", padding: 30, color: C.textMed }}>No conflicts.</div>
                    ) : (
                        <div style={{ overflowX: "auto" }}>
                            <table style={tbl.table}>
                                <thead>
                                    <tr>
                                        <th style={tbl.th}>Merchant coupon</th>
                                        <th style={tbl.th}>Code</th>
                                        <th style={tbl.th}>Reason</th>
                                        <th style={tbl.th}>Created</th>
                                        <th style={tbl.th}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {conflicts.map(cf => (
                                        <tr key={cf.id} className="rpt-row">
                                            <td style={tbl.td}>#{cf.merchant_coupon_id}</td>
                                            <td style={{ ...tbl.td, fontFamily: "ui-monospace, SFMono-Regular, monospace", fontWeight: 700 }}>{cf.attempted_code}</td>
                                            <td style={{ ...tbl.td, color: C.textMed }}>{cf.reason}</td>
                                            <td style={{ ...tbl.td, color: C.textMed, fontSize: 12, whiteSpace: "nowrap" }}>{formatDate(cf.createdAt)}</td>
                                            <td style={tbl.td}>
                                                <button
                                                    onClick={() => onResolveConflict(cf)}
                                                    style={btnSecondary}
                                                    disabled={resolvingConflictId === cf.id}
                                                >
                                                    {resolvingConflictId === cf.id ? "Resolving…" : "Mark resolved"}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </ModalBody>
            </Modal>

            {/* ─── Pending pushes Modal ─── */}
            {/* Redemptions claimed locally but not yet pushed to Odoo. The
                push worker drains 'pending' rows on its cron tick; 'failed'
                rows have exhausted retries and need ops attention. */}
            <Modal isOpen={pendingPushesOpen} toggle={() => setPendingPushesOpen(false)} size="lg">
                <ModalHeader toggle={() => setPendingPushesOpen(false)}>
                    Pending pushes to Merchant POS
                </ModalHeader>
                <ModalBody>
                    {pendingPushes.length === 0 ? (
                        <div style={{ textAlign: "center", padding: 30, color: C.textMed }}>
                            No redemptions waiting to sync.
                        </div>
                    ) : (
                        <div style={{ overflowX: "auto" }}>
                            <table style={tbl.table}>
                                <thead>
                                    <tr>
                                        <th style={tbl.th}>Coupon</th>
                                        <th style={tbl.th}>Order</th>
                                        <th style={tbl.th}>Amount</th>
                                        <th style={tbl.th}>Status</th>
                                        <th style={tbl.th}>Attempts</th>
                                        <th style={tbl.th}>Last error</th>
                                        <th style={tbl.th}>Created</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pendingPushes.map(r => (
                                        <tr key={r.id} className="rpt-row">
                                            <td style={tbl.td}>
                                                <div style={{ fontFamily: "ui-monospace, SFMono-Regular, monospace", fontWeight: 700 }}>
                                                    {r.coupon_code_snapshot}
                                                </div>
                                                {r.Coupon?.name && (
                                                    <div style={{ color: C.textLight, fontSize: 11 }}>{r.Coupon.name}</div>
                                                )}
                                            </td>
                                            <td style={tbl.td}>#{r.OrderId}</td>
                                            <td style={tbl.td}><strong>{r.amount_redeemed}</strong></td>
                                            <td style={tbl.td}>
                                                <Badge
                                                    color={r.merchant_push_status === "failed" ? C.coral : (C.purple || "#8E6DDC")}
                                                    bg={r.merchant_push_status === "failed" ? C.coralLight : "#EFE7FF"}
                                                >
                                                    {r.merchant_push_status}
                                                </Badge>
                                            </td>
                                            <td style={{ ...tbl.td, color: C.textMed, textAlign: "center" }}>
                                                {r.merchant_push_attempts}
                                            </td>
                                            <td style={{ ...tbl.td, color: C.textMed, fontSize: 11, maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.merchant_push_error || ""}>
                                                {r.merchant_push_error || <span style={{ color: C.textLight }}>—</span>}
                                            </td>
                                            <td style={{ ...tbl.td, color: C.textMed, fontSize: 12, whiteSpace: "nowrap" }}>
                                                {formatDate(r.createdAt)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </ModalBody>
            </Modal>

            {/* ─── Coupon View Modal (read-only details + redemption history) ─── */}
            {viewing && (
                <Modal isOpen={!!viewing} toggle={() => setViewing(null)} size="lg">
                    <ModalHeader toggle={() => setViewing(null)}>
                        Coupon — <span style={{ fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>{viewing.code}</span>
                        <SourceBadge source={viewing.source} />
                        <span style={{ marginLeft: 8 }}><ActiveBadge active={viewing.active} /></span>
                    </ModalHeader>
                    <ModalBody>
                        {/* Read-only details grid. Mirrors the fields surfaced
                            in the edit modal so admins see the same shape. */}
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
                            <ViewField label="NAME" value={viewing.name || "—"} />
                            <ViewField label="DISCOUNT TYPE" value={viewing.discount_type} />
                            <ViewField label="SCOPE" value={viewing.coupon_scope} />

                            {viewing.discount_type === "percentage" && (
                                <ViewField label="PERCENTAGE" value={viewing.percentage != null ? `${viewing.percentage}%` : "—"} />
                            )}
                            <ViewField label="AMOUNT (SAR)" value={viewing.amount} />
                            <ViewField label="MAX PER ORDER" value={viewing.max_amount_per_order ?? "—"} />

                            <ViewField label="MIN ORDER" value={viewing.min_order_amount ?? "—"} />
                            <ViewField label="REDEMPTIONS" value={`${viewing.current_redemptions}${viewing.max_redemptions ? ` / ${viewing.max_redemptions}` : ""}`} />
                            <ViewField label="BALANCE" value={viewing.remaining_balance != null ? `${viewing.remaining_balance} / ${viewing.amount}` : "—"} />

                            <ViewField label="STARTS" value={formatDate(viewing.starts_at)} />
                            <ViewField label="EXPIRES" value={formatDate(viewing.expires_at)} />
                            <ViewField label="CHANNELS" value={(viewing.channels || []).join(", ") || "—"} />
                        </div>

                        {viewing.description && (
                            <div style={{ marginBottom: 16, padding: 10, background: C.background2 || "#F5F5F7", borderRadius: 6, color: C.textMed, fontSize: 12 }}>
                                {viewing.description}
                            </div>
                        )}

                        {/* Paginated redemption history */}
                        <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 14, marginTop: 8 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                                <strong style={{ color: C.text }}>Redemption history</strong>
                                <span style={{ color: C.textLight, fontSize: 12 }}>
                                    {redemptions.total} total
                                </span>
                            </div>

                            {redemptionsLoading ? (
                                <div style={{ padding: 20 }}><Loader /></div>
                            ) : redemptions.rows.length === 0 ? (
                                <div style={{ textAlign: "center", padding: 20, color: C.textMed, fontSize: 13 }}>
                                    No redemptions yet.
                                </div>
                            ) : (
                                <>
                                    <div style={{ overflowX: "auto" }}>
                                        <table style={tbl.table}>
                                            <thead>
                                                <tr>
                                                    <th style={tbl.th}>Order</th>
                                                    <th style={tbl.th}>Amount</th>
                                                    <th style={tbl.th}>Status</th>
                                                    <th style={tbl.th}>Push status</th>
                                                    <th style={tbl.th}>When</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {redemptions.rows.map(r => (
                                                    <tr key={r.id} className="rpt-row">
                                                        <td style={tbl.td}>#{r.OrderId}</td>
                                                        <td style={tbl.td}><strong>{r.amount_redeemed}</strong></td>
                                                        <td style={{ ...tbl.td, textTransform: "capitalize", color: C.textMed }}>{r.status}</td>
                                                        <td style={tbl.td}>
                                                            <Badge
                                                                color={
                                                                    r.merchant_push_status === "success" ? C.green :
                                                                        r.merchant_push_status === "failed" ? C.coral :
                                                                            r.merchant_push_status === "not_applicable" ? C.textLight :
                                                                                (C.purple || "#8E6DDC")
                                                                }
                                                                bg={
                                                                    r.merchant_push_status === "success" ? (C.greenLight || "#D9F7E5") :
                                                                        r.merchant_push_status === "failed" ? C.coralLight :
                                                                            r.merchant_push_status === "not_applicable" ? (C.background2 || "#F5F5F7") :
                                                                                "#EFE7FF"
                                                                }
                                                            >
                                                                {r.merchant_push_status}
                                                            </Badge>
                                                        </td>
                                                        <td style={{ ...tbl.td, color: C.textMed, fontSize: 12, whiteSpace: "nowrap" }}>
                                                            {formatDate(r.createdAt)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Pagination — keep simple Prev/Next + label, no jump-to-page. */}
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12 }}>
                                        <button
                                            style={{ ...btnSecondary, opacity: redemptions.page <= 1 ? 0.5 : 1 }}
                                            disabled={redemptions.page <= 1 || redemptionsLoading}
                                            onClick={() => setRedemptions(r => ({ ...r, page: r.page - 1 }))}
                                        >
                                            ← Prev
                                        </button>
                                        <span style={{ color: C.textMed, fontSize: 12 }}>
                                            Page {redemptions.page} of {redemptions.totalPages}
                                        </span>
                                        <button
                                            style={{ ...btnSecondary, opacity: redemptions.page >= redemptions.totalPages ? 0.5 : 1 }}
                                            disabled={redemptions.page >= redemptions.totalPages || redemptionsLoading}
                                            onClick={() => setRedemptions(r => ({ ...r, page: r.page + 1 }))}
                                        >
                                            Next →
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </ModalBody>
                    <ModalFooter style={{ borderTop: `1px solid ${C.border}` }}>
                        <button onClick={() => setViewing(null)} style={btnSecondary}>Close</button>
                        {!readOnly && (
                            <button onClick={() => { setViewing(null); openEdit(viewing); }} style={btnPrimary}>Edit</button>
                        )}
                    </ModalFooter>
                </Modal>
            )}
        </Fragment>
    );
};

// Compact read-only field for the view modal. Local to this file — only used here.
const ViewField = ({ label, value }) => (
    <div>
        <div style={{ fontSize: 11, fontWeight: 600, color: C.textMed, marginBottom: 2 }}>{label}</div>
        <div style={{ fontSize: 13, color: C.text, textTransform: typeof value === "string" && /^[a-z_]+$/.test(value) ? "capitalize" : "none" }}>
            {value === "" || value == null ? "—" : value}
        </div>
    </div>
);

export default Coupons;
